import { IDevelopmentSequence } from "@/modules/assessments/hooks/use-get-invite-scale-development.hook";
import MarkdownPreviewCustom from "@/ui-components/commonComponents/MarkdownPreviewCustom/MarkdownPreviewCustom";
import SectionHeader from "@/shared/components/assessment/report/SectionHeader/SectionHeader";
import { InfoCircleFilled } from "@ant-design/icons";
import styles from "./styles.module.css";
import { ReactComponent as TipsSVG } from "@/assets/icons/tips.svg";
import { LanguagesIsoEnum } from "@/modules/assessments/types/test.interface";
import { DEFAULT_LOCALE, loadMessages } from "@/i18n";
import { FormattedMessage, useIntl } from "react-intl";
import DevelopEmptyState from "./DevelopEmptyState";
import DevelopLanguageControl from "./DevelopLanguageControl";
import classNames from "classnames";

interface DevelopProps {
  developmentSequences: IDevelopmentSequence[];
  availableLocales?: LanguagesIsoEnum[];
  locale?: LanguagesIsoEnum;
  // Lets the empty state offer a way back to English. Omitted on the print path (no interaction).
  onLocaleChange?: (locale: LanguagesIsoEnum) => void;
}

export default function Develop({ developmentSequences, availableLocales, locale, onLocaleChange }: DevelopProps) {
  const intl = useIntl();

  // Gate only when coverage is known and excludes the locale; undefined coverage means don't gate.
  const isLocaleUnavailable =
    Boolean(locale) &&
    locale !== DEFAULT_LOCALE &&
    availableLocales !== undefined &&
    !availableLocales.includes(locale!);

  if (isLocaleUnavailable) {
    return <DevelopEmptyState onViewInEnglish={onLocaleChange ? () => onLocaleChange(DEFAULT_LOCALE) : undefined} />;
  }

  if (developmentSequences.length === 0) {
    return null;
  }

  const groupedBySection = developmentSequences.reduce((acc, sequence) => {
    if (!acc[sequence.sectionType]) {
      acc[sequence.sectionType] = [];
    }
    acc[sequence.sectionType].push(sequence);
    return acc;
  }, {} as Record<string, IDevelopmentSequence[]>);

  const canSwitchLanguage = Boolean(onLocaleChange) && !!availableLocales && availableLocales.length > 1;

  return (
    <div className={styles.developContainer}>
      <SectionHeader
        title={intl.formatMessage({ id: "report.develop.title", defaultMessage: "Development Insights" })}
        // Warm the chosen locale's bundle before printing, as a safety net for the rare case where
        // print fires before the print tree's own locale load has resolved.
        onBeforePrint={async () => {
          await loadMessages(locale ?? DEFAULT_LOCALE);
        }}
        actions={
          canSwitchLanguage ? (
            <DevelopLanguageControl
              availableLocales={availableLocales!}
              locale={locale ?? DEFAULT_LOCALE}
              onChange={onLocaleChange!}
            />
          ) : undefined
        }
      />
      <div className={styles.helpText}>
        <InfoCircleFilled className={styles.helpIcon} aria-hidden="true" />
        <p>
          <FormattedMessage
            id="report.develop.intro"
            defaultMessage="Based on the assessment, results are categorized along with tips for leveraging strengths and improving in opportunity areas."
          />
        </p>
      </div>
      {Object.keys(groupedBySection).map((sectionType) => {
        const sequences = groupedBySection[sectionType];
        const sectionName = sequences[0]?.sectionName;

        return (
          <section className={styles.sectionContainer} key={sectionType}>
            <h2 className={styles.sectionTitle}>{sectionName}</h2>
            {sequences.map((sequence) => {
              const scaleHeaderId = `${sequence.sectionType}-${sequence.rank}-title`;

              return (
                <article
                  aria-labelledby={scaleHeaderId}
                  key={`${sequence.scaleName.value}-${sequence.rank}`}
                  className={styles.scaleCard}
                >
                  <div className={styles.headerAndDescriptionWrapper}>
                    <header className={styles.scaleHeader}>
                      <h3 id={scaleHeaderId} className={styles.scaleTitle}>
                        {sequence.scaleName.value ?? ""}
                      </h3>
                      <p className={classNames(styles.badge, styles[sequence.scoreType])}>{sequence.scoreTypeLabel}</p>
                    </header>
                    <div className={styles.descriptionContainer}>
                      <MarkdownPreviewCustom source={sequence.content.description.value ?? ""} />
                    </div>
                  </div>
                  <section className={styles.tipsContainer}>
                    <div className={styles.headerWrapper}>
                      <TipsSVG aria-hidden={true} />
                      <h4 className={styles.tipsTitle}>
                        <FormattedMessage id="report.develop.tipsLabel" defaultMessage="Tips:" />
                      </h4>
                    </div>
                    <ul className={styles.tipsList}>
                      {sequence.content.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className={styles.tipItem}>
                          <MarkdownPreviewCustom source={tip.value ?? ""} className="tips-markdown" />
                        </li>
                      ))}
                    </ul>
                  </section>
                </article>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}
